import React, { useState } from 'react';
import DashboardLayout from "../layout/index";
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../constants/approutes';

const SmartVitalForm = () => {
  const [formData, setFormData] = useState({
    heightCm: '',
    feet: '',
    inches: '',
    weight: '',
    diabetes: '',
    smoke: ''
  });
  const [heightUnit, setHeightUnit] = useState('cm');
  const [weightUnit, setWeightUnit] = useState('kg');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
    const location = useLocation();
  const { patientCode } = location.state || {};

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'heightCm':
        if (!value) newErrors.heightCm = 'Height is required';
        else if (!/^[0-9]+$/.test(value)) newErrors.heightCm = 'Only whole numbers allowed';
        else if (value < 120 || value > 210) newErrors.heightCm = 'Height must be between 120-210 cm';
        else delete newErrors.heightCm;
        break;
      case 'feet':
        if (!value) newErrors.feet = 'Feet is required';
        else if (!/^[0-9]+$/.test(value)) newErrors.feet = 'Only whole numbers allowed';
        else if (value < 4 || value > 7) newErrors.feet = 'Must be between 4-7 feet';
        else delete newErrors.feet;
        break;
      case 'inches':
        if (!value) newErrors.inches = 'Inches is required';
        else if (!/^[0-9]+$/.test(value)) newErrors.inches = 'Only whole numbers allowed';
        else if (value < 0 || value >= 12) newErrors.inches = 'Must be between 0-11 inches';
        else delete newErrors.inches;
        break;
      case 'weight':
        if (!value) newErrors.weight = 'Weight is required';
        else if (!/^[0-9]+$/.test(value)) newErrors.weight = 'Only whole numbers allowed';
        else if (value < 20 || value > 1000) newErrors.weight = 'Weight must be between 20-1000 kg';
        else delete newErrors.weight;
        break;
      case 'diabetes':
      case 'smoke':
        if (!value) newErrors[name] = 'Please select an option';
        else delete newErrors[name];
        break;
    }

    setErrors(newErrors);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const requiredFields = heightUnit === 'cm'
      ? ['heightCm', 'weight', 'diabetes', 'smoke']
      : ['feet', 'inches', 'weight', 'diabetes', 'smoke'];

    let hasErrors = false;
    requiredFields.forEach(field => {
      if (!formData[field]) {
        validateField(field, '');
        hasErrors = true;
      }
    });

    if (hasErrors || Object.keys(errors).length > 0) return;

    // Process form data
    let finalValues = { ...formData, heightUnit, weightUnit };
    if (heightUnit === 'ft/inch') {
      const feet = parseInt(formData.feet, 10) || 0;
      const inches = parseInt(formData.inches, 10) || 0;
      finalValues.heightCm = feet * 30.48 + inches * 2.54;
    }
  localStorage.setItem("smartVitalFormData", JSON.stringify(finalValues));
     navigate(APP_ROUTES.VIDEO_CAPTURE, {
              state: {
                patientCode: patientCode,
              },
            });
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-3">

              <h1 className="text-2xl font-semibold text-center text-[#004276]">
                Smart Vital <span className="text-[#3cba84]">Data Capture</span>
              </h1>
            </div>
          </div>

          {/* Instructions Card */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-2xl p-6 mb-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-green-700">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Ensure good lighting, clear face visibility, and stay still during capture</span>
                </div>
                <div className="flex items-center gap-2 text-red-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Don't use filters, makeup, or shaky cameras</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl shadow-xl p-8 space-y-8">
            {/* Physical Measurements Section */}
            <div className="space-y-6">
              {/* <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600">1</span>
                </div>
                Physical Measurements
              </h2> */}

              <div className="grid md:grid-cols-2 gap-6">
                {/* Height */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    Height <span className="text-red-500">*</span>
                  </label>

                  <div className="flex gap-3">
                    {heightUnit === 'cm' ? (
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Enter height"
                          value={formData.heightCm}
                          onChange={(e) => handleInputChange('heightCm', e.target.value)}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.heightCm ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                            }`}
                        />
                        {errors.heightCm && <p className="text-red-500 text-xs mt-1">{errors.heightCm}</p>}
                      </div>
                    ) : (
                      <>
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Feet"
                            value={formData.feet}
                            onChange={(e) => handleInputChange('feet', e.target.value)}
                            className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.feet ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                              }`}
                          />
                          {errors.feet && <p className="text-red-500 text-xs mt-1">{errors.feet}</p>}
                        </div>
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Inches"
                            value={formData.inches}
                            onChange={(e) => handleInputChange('inches', e.target.value)}
                            className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.inches ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                              }`}
                          />
                          {errors.inches && <p className="text-red-500 text-xs mt-1">{errors.inches}</p>}
                        </div>
                      </>
                    )}

                    <select
                      value={heightUnit}
                      onChange={(e) => setHeightUnit(e.target.value)}
                      className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[100px]"
                    >
                      <option value="cm">cm</option>
                      <option value="ft/inch">ft/inch</option>
                    </select>
                  </div>
                </div>

                {/* Weight */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    Weight <span className="text-red-500">*</span>
                  </label>

                  <div className="flex gap-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Enter weight"
                        value={formData.weight}
                        onChange={(e) => handleInputChange('weight', e.target.value)}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.weight ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                      />
                      {errors.weight && <p className="text-red-500 text-xs mt-1">{errors.weight}</p>}
                    </div>

                    <select
                      value={weightUnit}
                      onChange={(e) => setWeightUnit(e.target.value)}
                      className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[80px]"
                    >
                      <option value="kg">kg</option>
                      <option value="lbs">lbs</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Health Information Section */}
            <div className="space-y-6">
              {/* <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-green-600">2</span>
                </div>
                Health Information
              </h2> */}

              <div className="grid md:grid-cols-2 gap-6">
                {/* Diabetes */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    Diabetes <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={formData.diabetes}
                    onChange={(e) => handleInputChange('diabetes', e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.diabetes ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <option value="">Select</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                  {errors.diabetes && <p className="text-red-500 text-xs mt-1">{errors.diabetes}</p>}
                </div>

                {/* Smoke */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    Smoke <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={formData.smoke}
                    onChange={(e) => handleInputChange('smoke', e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.smoke ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <option value="">Select</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                  {errors.smoke && <p className="text-red-500 text-xs mt-1">{errors.smoke}</p>}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                onClick={handleSubmit}
                className="w-full bg-gradient-to-r from-[#004172] to-[#3cba84] hover:from-blue-900 hover:to-green-600 text-white font-semibold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-200"
              >
                Continue to Video Capture →
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SmartVitalForm;